/**
 * @file: This file holds the catalogue of the Style guide page.
 *
 * The page shows one sample of every shared element. The tables here name the
 * tokens, the sanctioned buttons and the rules of the first section. The values
 * are text for the reader. The samples themselves read the real tokens with
 * var(), so a new value in client/style/tokens.less changes every sample.
 *
 * The spec beside the component reads client/style/tokens.less and compares it
 * with the token tables. Thus the page cannot drift from the tokens.
 */

/**
 * A square of the colour stands beside each of these. A token that the dark
 * block of client/style/tokens.less gives a second value also holds `dark`,
 * and the page draws a second square for it. A token that is an alias of a
 * Bootstrap property has no `dark`, because Bootstrap changes the value.
 */
const COLOUR_TOKENS = Object.freeze([
  Object.freeze({ name: '--jumbotron-surface', value: '#ffffff', dark: '#212529' }),
  Object.freeze({ name: '--jumbotron-primary', value: '#007bff', dark: '#3d8bfd' }),
  Object.freeze({ name: '--jumbotron-primary-ink', value: '#0056b3', dark: 'var(--bs-primary-text-emphasis)' }),
  Object.freeze({ name: '--jumbotron-primary-rgb', value: '0, 123, 255', swatch: 'rgb(var(--jumbotron-primary-rgb))', dark: '61, 139, 253' }),
  Object.freeze({ name: '--jumbotron-accent', value: '#f57c00', dark: '#ffa040' }),
  Object.freeze({ name: '--jumbotron-warning', value: 'var(--bs-warning)' }),
  Object.freeze({ name: '--jumbotron-danger', value: 'var(--bs-danger)' }),
  Object.freeze({ name: '--jumbotron-warning-rgb', value: '255, 193, 7', swatch: 'rgb(var(--jumbotron-warning-rgb))', dark: '255, 193, 7' }),
  Object.freeze({ name: '--jumbotron-text', value: 'var(--bs-body-color)' }),
  Object.freeze({ name: '--jumbotron-muted', value: 'var(--bs-secondary)', dark: 'var(--bs-secondary-color)' }),
  Object.freeze({ name: '--jumbotron-line', value: 'var(--bs-border-color)' }),
  Object.freeze({ name: '--jumbotron-row-line', value: '#f1f3f5', dark: '#343a40' }),
  Object.freeze({ name: '--jumbotron-edge', value: '#adb5bd', dark: '#495057' }),
  Object.freeze({ name: '--jumbotron-stripe', value: 'var(--bs-tertiary-bg)' }),
  Object.freeze({ name: '--jumbotron-toolbar-end', value: 'var(--bs-secondary-bg)' }),
  Object.freeze({ name: '--jumbotron-ground', value: '#f1f3f5', dark: '#343a40' }),
  Object.freeze({ name: '--jumbotron-page-ground', value: '#e0e0e0', dark: '#16191d' }),
  Object.freeze({ name: '--jumbotron-invalid-ground', value: '#ffdddd', dark: '#4a1c20' }),
  Object.freeze({ name: '--jumbotron-amber', value: '#b58100', dark: '#ffcd39' }),
  Object.freeze({ name: '--jumbotron-live', value: '#28a745', dark: '#3dd16a' }),
  Object.freeze({ name: '--jumbotron-backdrop', value: 'rgba(52, 58, 64, 0.5)', dark: 'rgba(0, 0, 0, 0.6)' }),
  Object.freeze({ name: '--jumbotron-selected-ground', value: 'rgba(0, 123, 255, 0.08)', dark: 'rgba(var(--jumbotron-primary-rgb), 0.18)' }),
  Object.freeze({ name: '--jumbotron-ok-ink', value: '#14602c', dark: 'var(--bs-success-text-emphasis)' }),
  Object.freeze({ name: '--jumbotron-ok-ground', value: 'rgba(40, 167, 69, 0.14)', dark: 'var(--bs-success-bg-subtle)' }),
  Object.freeze({ name: '--jumbotron-warn-ink', value: '#664d03', dark: 'var(--bs-warning-text-emphasis)' }),
  Object.freeze({ name: '--jumbotron-warn-ground', value: 'rgba(255, 193, 7, 0.18)', dark: 'var(--bs-warning-bg-subtle)' }),
  Object.freeze({ name: '--jumbotron-bad-ink', value: '#842029', dark: 'var(--bs-danger-text-emphasis)' }),
  Object.freeze({ name: '--jumbotron-bad-ground', value: 'rgba(220, 53, 69, 0.12)', dark: 'var(--bs-danger-bg-subtle)' })
])

/**
 * A line of text set in the font stands beside this one. The app has one font.
 */
const FONT_TOKENS = Object.freeze([
  Object.freeze({ name: '--jumbotron-font', value: 'var(--bs-body-font-family)' })
])

/**
 * A line of text set in the size stands beside each of these. The scale has
 * six steps, and the order here is the order of the scale.
 */
const TYPE_TOKENS = Object.freeze([
  Object.freeze({ name: '--jumbotron-kind-size', value: '0.6875rem' }),
  Object.freeze({ name: '--jumbotron-small-size', value: '0.8125rem' }),
  Object.freeze({ name: '--jumbotron-text-size', value: '0.875rem' }),
  Object.freeze({ name: '--jumbotron-heading-size', value: '1.0625rem' }),
  Object.freeze({ name: '--jumbotron-title-size', value: '1.25rem' }),
  Object.freeze({ name: '--jumbotron-value-size', value: '1.5rem' })
])

/**
 * A drawn box stands beside each of these. `demo` is the modifier class of the
 * box, so the template picks the drawing from the data and not from a switch.
 */
const MEASURE_TOKENS = Object.freeze([
  Object.freeze({ name: '--jumbotron-surface-gap', value: '14px', demo: 'is-gap' }),
  Object.freeze({ name: '--jumbotron-radius', value: '12px', demo: 'is-radius' }),
  Object.freeze({ name: '--jumbotron-radius-menu', value: '8px', demo: 'is-radius-menu' }),
  Object.freeze({ name: '--jumbotron-radius-small', value: '4px', demo: 'is-radius-small' })
])

/**
 * A box with the shadow stands beside each of these. There are three depths and
 * no more: a panel, a card above a dimmed page, and a menu.
 */
const SHADOW_TOKENS = Object.freeze([
  Object.freeze({ name: '--jumbotron-shadow', value: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05)' }),
  Object.freeze({ name: '--jumbotron-shadow-raised', value: '0 10px 30px rgba(0, 0, 0, 0.22)' }),
  Object.freeze({ name: '--jumbotron-shadow-menu', value: '0 6px 18px rgba(0, 0, 0, 0.18)' })
])

/**
 * The layers of the app. A number that only orders the children of one element
 * is not a layer and is not here. A page that must stand above the menu reads
 * this token and adds to it with calc().
 */
const LAYER_TOKENS = Object.freeze([
  Object.freeze({ name: '--jumbotron-z-menu', value: '1050' })
])

/** Every token of the app, in one list. The spec reads this one. */
const TOKENS = Object.freeze(
  [].concat(COLOUR_TOKENS, FONT_TOKENS, TYPE_TOKENS, MEASURE_TOKENS, SHADOW_TOKENS, LAYER_TOKENS)
)

/**
 * The sanctioned button combinations. A page picks a line from this table and
 * writes the class string. A combination that is not here is not sanctioned.
 */
const BUTTONS = Object.freeze([
  Object.freeze({
    className: 'btn btn-sm btn-outline-primary',
    label: 'Add media',
    use: 'The default action of a surface.'
  }),
  Object.freeze({
    className: 'btn btn-sm btn-outline-secondary',
    label: 'Close',
    use: 'A neutral action, such as Close or Cancel.'
  }),
  Object.freeze({
    className: 'btn btn-sm btn-primary',
    label: 'Save',
    use: 'The one primary action of a surface, such as Save.'
  }),
  Object.freeze({
    className: 'btn btn-sm btn-discreet-danger',
    label: 'Delete',
    use: 'Delete. The rule is in client/style/button-discreet.less.'
  })
])

/** The rule of the whole page. Every other section shows one part of it. */
const RULE = 'Every panel of a page is a surface with a panel head. The controls in it, buttons, inputs, selects and the close button, are Bootstrap and read the same tokens.'

/** The cases that the style guide refuses. Each entry is one case. */
const FORBIDDEN = Object.freeze([
  'Do not write a raw number for a colour, a font size, a radius or a shadow. Read a token.',
  'Do not add a fourth tint. The three conditions are ok, warn and bad.',
  'Do not use a Bootstrap card. A panel is a surface.',
  'Do not put more than one primary button on a surface.',
  'Do not set a colour on an element that is not data. The colour of a scene and the colour of a label are data, and the template sets them inline.'
])

/** The steps for a rule that no shared element holds yet. */
const WHEN_IT_DOES_NOT_FIT = Object.freeze([
  'Write the rule in the LESS file of your component, under its root class.',
  'When a second page needs the same rule, the rule becomes an element. An element is a mixin in client/style/elements/, one class in client/style/elements-classes.less and one sample on this page.',
  'A value that has to stay a literal goes on a list in client/style/tokens.spec.js with its reason.'
])

/**
 * The skeleton of a panel. The page shows this text beside the same markup as
 * a drawn panel, so the reader sees the result of every line. The reader copies
 * the text and replaces my-panel with the name of the component.
 */
const RECIPE = `<div class="jumbotron-surface my-panel">
  <div class="jumbotron-panel-head">
    <span class="jumbotron-heading">Panel</span>
    <button type="button" class="btn btn-sm btn-outline-primary">Action</button>
  </div>
  <div class="my-panel-body">
    <span class="jumbotron-note">The rules of this panel are under .my-panel in the LESS file of the component.</span>
  </div>
</div>`

/** The three rules for the LESS file of a component. */
const LESS_RULES = Object.freeze([
  'Put one LESS file beside the component, and name it <name>.component.less. The JS of the component imports it.',
  'Nest every rule under the root class of the component. The shared classes land last in the bundle, and two classes beat one class in every order. The names also stay out of the global space.',
  'Read a token for a colour, a font, a radius and a shadow. The spec scans every LESS file under client/components and client/style, and it fails on a literal.'
])

/** The parts of the app that do not read the shared elements yet. */
const NOT_SHARED = Object.freeze([
  'The calendar toolbar is built in JavaScript by FullCalendar.',
  'Four selection class names still exist: is-selected, is-on, active and is-lit.',
  'Drag and drop has no touch support.'
])

/** Three colours for the dot samples. A real page reads the colour of a scene. */
const SAMPLE_COLOURS = Object.freeze(['#ff851b', '#0074d9', '#2ecc40'])

/** Three shares for the bar samples. A real page reads the share of a row. */
const SAMPLE_SHARES = Object.freeze([100, 62, 20])

export {
  COLOUR_TOKENS,
  FONT_TOKENS,
  TYPE_TOKENS,
  MEASURE_TOKENS,
  SHADOW_TOKENS,
  LAYER_TOKENS,
  TOKENS,
  BUTTONS,
  RULE,
  FORBIDDEN,
  WHEN_IT_DOES_NOT_FIT,
  RECIPE,
  LESS_RULES,
  NOT_SHARED,
  SAMPLE_COLOURS,
  SAMPLE_SHARES
}
